import React, { useState } from "react";
import { Formik, Form } from "formik";
import { Alert, Badge, Button, Card, Col, Container, Form as BootstrapForm, Row } from "components/ui/BootstrapCompat";
import { useNavigate } from "react-router-dom";
import CustomInput from "components/CustomInput";
import { sendVerifyCode, verifyCode } from "services/main/public.service";

type VerifyValues = {
  identifier: string;
  code: string;
};

type VerifyChannel = "email" | "phone";

const initialValues: VerifyValues = {
  identifier: "",
  code: "",
};

const channelOptions: { label: string; value: VerifyChannel }[] = [
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
];

const getErrorMessage = (error: any) => {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ"
  );
};

export default function VerifyPage() {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<VerifyChannel>("email");
  const [otpSent, setOtpSent] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusVariant, setStatusVariant] = useState<"success" | "danger" | "warning">("success");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleSendCode = async (identifier: string) => {
    if (!identifier.trim()) {
      setStatusVariant("danger");
      setStatusMessage("ກະລຸນາປ້ອນ identifier ກ່ອນສົ່ງ OTP");
      return;
    }

    setIsSending(true);
    setStatusMessage("");

    try {
      await sendVerifyCode({ identifier: identifier.trim(), channel });
      setOtpSent(true);
      setStatusVariant("success");
      setStatusMessage(`ສົ່ງ OTP ໄປທີ່ຊ່ອງທາງ ${channel} ແລ້ວ`);
    } catch (error: any) {
      setStatusVariant("danger");
      setStatusMessage(getErrorMessage(error));
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async (values: VerifyValues) => {
    const identifier = values.identifier.trim();
    const code = values.code.trim();

    if (!identifier || !code) {
      setStatusVariant("danger");
      setStatusMessage("ກະລຸນາປ້ອນ identifier ແລະ OTP ໃຫ້ຄົບຖ້ວນ");
      return;
    }

    setIsVerifying(true);
    setStatusMessage("");

    try {
      const response = await verifyCode({ identifier, channel, code });
      setStatusVariant("success");
      setStatusMessage("ຢືນຢັນສຳເລັດແລ້ວ");

      if (response?.emailVerified !== undefined) {
        setEmailVerified(Boolean(response.emailVerified));
      }
      if (response?.phoneVerified !== undefined) {
        setPhoneVerified(Boolean(response.phoneVerified));
      }

      if (channel === "email") {
        setEmailVerified(true);
      }
      if (channel === "phone") {
        setPhoneVerified(true);
      }
    } catch (error: any) {
      setStatusVariant("danger");
      const message = getErrorMessage(error);
      setStatusMessage(message);
      if (message.toLowerCase().includes("ໝົດອາຍຸ") || message.toLowerCase().includes("expired")) {
        setOtpSent(false);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f4f7fb] py-10 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="mb-4 d-flex flex-column gap-2">
                  <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <div>
                      <h1 className="h3 mb-1">Verify Contact</h1>
                      <p className="text-muted mb-0">
                        ເລືອກຊ່ອງທາງເພື່ອສົ່ງ OTP ແລະຢືນຢັນ email ຫຼື phone.
                      </p>
                    </div>
                    <Button variant="link" onClick={() => navigate("/login")}>ກັບໄປໜ້າ Login</Button>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <Badge variant={emailVerified ? "success" : "secondary"}>
                      Email: {emailVerified ? "ຢືນຢັນແລ້ວ" : "ຍັງບໍ່ໄດ້ຢືນຢັນ"}
                    </Badge>
                    <Badge variant={phoneVerified ? "success" : "secondary"}>
                      Phone: {phoneVerified ? "ຢືນຢັນແລ້ວ" : "ຍັງບໍ່ໄດ້ຢືນຢັນ"}
                    </Badge>
                  </div>
                </div>

                {statusMessage && (
                  <Alert variant={statusVariant} className="mb-3">
                    {statusMessage}
                  </Alert>
                )}

                <Formik
                  initialValues={initialValues}
                  validate={(values) => {
                    const errors: Record<string, string> = {};
                    if (!values.identifier.trim()) {
                      errors.identifier = "ກະລຸນາປ້ອນ identifier";
                    }
                    if (otpSent && !values.code.trim()) {
                      errors.code = "ກະລຸນາປ້ອນ OTP";
                    }
                    return errors;
                  }}
                  onSubmit={async (values) => {
                    await handleVerifyCode(values);
                  }}
                >
                  {({ values }) => (
                    <Form>
                      <div className="mb-3">
                        <CustomInput
                          require
                          label="Identifier"
                          name="identifier"
                          type="text"
                          placeholder="streamer01 ຫຼື email ຫຼື phone"
                        />
                      </div>

                      <BootstrapForm.Group className="mb-3">
                        <BootstrapForm.Label>ຊ່ອງທາງ OTP</BootstrapForm.Label>
                        <BootstrapForm.Control
                          as="select"
                          value={channel}
                          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                            setChannel(event.target.value as VerifyChannel);
                          }}
                        >
                          {channelOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </BootstrapForm.Control>
                      </BootstrapForm.Group>

                      <div className="d-flex flex-column flex-sm-row gap-2 mb-4">
                        <Button
                          variant="primary"
                          type="button"
                          disabled={isSending || !values.identifier.trim()}
                          onClick={() => handleSendCode(values.identifier)}
                        >
                          {isSending ? "ກຳລັງສົ່ງ OTP..." : "ສົ່ງ OTP"}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={() => {
                            setOtpSent(false);
                            setStatusMessage("");
                          }}
                        >
                          ຣີເຊັດ
                        </Button>
                      </div>

                      {otpSent && (
                        <div className="mb-4">
                          <CustomInput
                            require
                            label="OTP Code"
                            name="code"
                            type="text"
                            placeholder="123456"
                          />
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              variant="success"
                              type="submit"
                              disabled={isVerifying || !values.code.trim()}
                            >
                              {isVerifying ? "ກຳລັງຢືນຢັນ..." : "ຢືນຢັນລະຫັດ"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </main>
  );
}
